import { RefinementCtx } from 'zod';
import { EmployeeRole } from '@/api/apis/resources';
import { NameByValue } from '@/types';
import { ProfileFormType } from './types';
import { COUNTRY_SCHEMA, getPhoneSchema, addCustomZodErrors } from '@/utils/helpers';
import { getCountryHasStates } from '@/hooks/useCountrySubdivisionInfo';

export const createProfileFormValidation =
  (composedSubdivisionsList: Record<string, NameByValue[]>) =>
  (
    {
      clinicalCoordinatorType,
      country,
      state,
      scrubSizeTop,
      scrubSizeBottom,
      weight,
      locationPreference,
      baseLocationId,
      roles,
      organType,
      tsaPrecheckNumber,
      phone,
    }: ProfileFormType,
    ctx: RefinementCtx,
  ) => {
    const isClinicalCoordinatorTypeValidation =
      roles.includes(EmployeeRole.ClinicalCoordinator) && !clinicalCoordinatorType;

    const countryHasStates = getCountryHasStates(composedSubdivisionsList, country);
    const isStateValidation = countryHasStates && !state;

    const isUniformisUniformMeasurementsValidation =
      roles.includes(EmployeeRole.ClinicalSpecialist) || roles.includes(EmployeeRole.RetrievalSurgeon);

    const rolesForOrganValidation = [
      EmployeeRole.ClinicalSpecialist,
      EmployeeRole.RetrievalSurgeon,
      EmployeeRole.OrganLeadership,
      EmployeeRole.OrganManagement,
    ];

    const isOrganValidation = roles.some((role) => rolesForOrganValidation.includes(role)) && !organType.length;

    const isLocationPreferencesValidation =
      (roles.includes(EmployeeRole.ClinicalSpecialist) ||
        roles.includes(EmployeeRole.RetrievalSurgeon) ||
        roles.includes(EmployeeRole.ClinicalLead)) &&
      !locationPreference;

    const isBaseLocationValidation =
      (roles.includes(EmployeeRole.ClinicalSpecialist) ||
        roles.includes(EmployeeRole.RetrievalSurgeon) ||
        roles.includes(EmployeeRole.ClinicalLead)) &&
      !baseLocationId;

    const isTsaPrecheckNumberValidation = tsaPrecheckNumber !== null && tsaPrecheckNumber?.trim() !== '';

    const phoneSchemaResult = getPhoneSchema({ isRequired: true }).safeParse(phone);

    const countrySchemaResult = COUNTRY_SCHEMA.safeParse(country);

    if (isClinicalCoordinatorTypeValidation) {
      addCustomZodErrors({
        ctx,
        path: ['clinicalCoordinatorType'],
        message: 'Provide TC level',
      });
    }

    if (!countrySchemaResult.success) {
      addCustomZodErrors({
        ctx,
        path: ['country'],
        message: countrySchemaResult.error.issues[0].message,
      });
    }

    if (isStateValidation) {
      addCustomZodErrors({
        ctx,
        path: ['state'],
        message: 'Select state',
      });
    }

    if (isOrganValidation) {
      addCustomZodErrors({
        ctx,
        path: ['organType'],
        message: 'Select organ',
      });
    }

    if (isUniformisUniformMeasurementsValidation) {
      if (!scrubSizeTop) {
        addCustomZodErrors({
          ctx,
          path: ['scrubSizeTop'],
          message: 'Select size top',
        });
      }

      if (!scrubSizeBottom) {
        addCustomZodErrors({
          ctx,
          path: ['scrubSizeBottom'],
          message: 'Select size bottom',
        });
      }

      if (!weight) {
        addCustomZodErrors({
          ctx,
          path: ['weight'],
          message: 'Provide weight',
        });
      }
    }

    if (isLocationPreferencesValidation) {
      addCustomZodErrors({
        ctx,
        path: ['locationPreference'],
        message: 'Select location',
      });
    }

    if (isBaseLocationValidation) {
      addCustomZodErrors({
        ctx,
        path: ['baseLocationId'],
        message: 'Select base location',
      });
    }

    if (!phoneSchemaResult.success) {
      addCustomZodErrors({
        ctx,
        path: ['phone'],
        message: phoneSchemaResult.error.issues[0].message,
      });
    }

    if (isTsaPrecheckNumberValidation) {
      const validFormat = /^[a-zA-Z0-9]+$/.test(tsaPrecheckNumber);
      const validLength = tsaPrecheckNumber.length >= 9 && tsaPrecheckNumber.length <= 10;
      const validPrefix = /^(TT|TE|AC)/.test(tsaPrecheckNumber);

      if (!validFormat || !validLength || !validPrefix) {
        addCustomZodErrors({
          ctx,
          path: ['tsaPrecheckNumber'],
          message: 'TSA PreCheck number must be 9-10 alphanumeric characters and begin with TT, TE, or AC',
        });
      }
    }
  };
